import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Building2, Plus, Pencil, Trash2, ChevronRight } from "lucide-react";
import { type Company, LEGAL_FORM_LABELS, LEGAL_FORM_ICONS } from "@/hooks/useCompanies";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface CompanySelectorProps {
  companies: Company[];
  selectedCompanyId: string | null;
  onSelect: (companyId: string) => void;
  onAdd: () => void;
  onEdit: (company: Company) => void;
  onDelete: (company: Company) => void;
  isLoading?: boolean;
}

export function CompanySelector({
  companies,
  selectedCompanyId,
  onSelect,
  onAdd,
  onEdit,
  onDelete,
  isLoading,
}: CompanySelectorProps) {
  if (isLoading) {
    return (
      <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="h-20 w-40 shrink-0 rounded-lg bg-muted animate-pulse"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
      {companies.map((company) => {
        const isSelected = company.id === selectedCompanyId;
        const icon = LEGAL_FORM_ICONS[company.legal_form];

        return (
          <Card
            key={company.id}
            className={cn(
              "shrink-0 cursor-pointer transition-all hover:shadow-md group relative",
              isSelected
                ? "border-primary ring-2 ring-primary/20 bg-primary/5"
                : "hover:border-primary/50"
            )}
            onClick={() => onSelect(company.id)}
          >
            <CardContent className="p-4 min-w-[160px]">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{icon}</span>
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate max-w-[100px]">
                      {company.name}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {LEGAL_FORM_LABELS[company.legal_form]}
                    </p>
                  </div>
                </div>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(company); }}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Modifier
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={(e) => { e.stopPropagation(); onDelete(company); }}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Supprimer
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {isSelected && (
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-8 h-1 bg-primary rounded-full" />
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Add company card */}
      <Card
        className="shrink-0 cursor-pointer transition-all hover:shadow-md border-dashed hover:border-primary/50"
        onClick={onAdd}
      >
        <CardContent className="p-4 min-w-[140px] flex flex-col items-center justify-center gap-2 h-full">
          <div className="p-2 rounded-full bg-primary/10">
            <Plus className="h-5 w-5 text-primary" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            Ajouter
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
